import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, Trash2, Calendar as CalendarIcon, GripVertical, ChevronDown,
  Copy, MessageSquare, Paperclip, X, Send, Download, FileText, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { toast } from 'sonner';
import api from '@/lib/api';

// Monday-like status swatches — pill + full-row soft background
const STATUS_COLORS = {
  todo:        { label: 'À faire',   bg: 'bg-zinc-700',    text: 'text-zinc-100', row: 'bg-zinc-900/40 hover:bg-zinc-900/60',            border: 'border-l-zinc-600' },
  in_progress: { label: 'En cours',  bg: 'bg-amber-500',   text: 'text-zinc-950', row: 'bg-amber-500/10 hover:bg-amber-500/15',           border: 'border-l-amber-500' },
  done:        { label: 'Fait',      bg: 'bg-emerald-500', text: 'text-zinc-950', row: 'bg-emerald-500/10 hover:bg-emerald-500/15',       border: 'border-l-emerald-500' },
  blocked:     { label: 'Bloqué',    bg: 'bg-rose-500',    text: 'text-zinc-50',  row: 'bg-rose-500/10 hover:bg-rose-500/15',             border: 'border-l-rose-500' },
};
const STATUS_ORDER = ['todo', 'in_progress', 'done', 'blocked'];

const OWNERS = [
  { key: 'arthur',    label: 'Arthur',    accent: 'border-l-blue-500',   dot: 'bg-blue-500' },
  { key: 'sebastien', label: 'Sébastien', accent: 'border-l-purple-500', dot: 'bg-purple-500' },
];

const fmtBytes = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
};

function StatusPill({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const cur = STATUS_COLORS[value] || STATUS_COLORS.todo;
  return (
    <div className="relative inline-block" data-testid={`status-pill-${value}`}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        className={`${cur.bg} ${cur.text} px-3 py-1 rounded-sm text-xs font-semibold min-w-[92px] text-center inline-flex items-center justify-center gap-1 hover:brightness-110 transition`}
      >
        {cur.label}
        <ChevronDown className="w-3 h-3 opacity-60" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 bg-zinc-900 border border-zinc-800 rounded-sm shadow-2xl z-20 py-1 min-w-[120px]">
            {STATUS_ORDER.map(s => (
              <button
                key={s}
                onClick={(e) => { e.stopPropagation(); onChange(s); setOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-800 flex items-center gap-2"
                data-testid={`status-option-${s}`}
              >
                <span className={`inline-block w-2 h-2 rounded-full ${STATUS_COLORS[s].bg}`} />
                <span className="text-zinc-200">{STATUS_COLORS[s].label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function DueDateCell({ value, onChange }) {
  const overdue = value && (new Date(value) < new Date(new Date().toDateString()));
  return (
    <div className="flex items-center gap-2">
      <CalendarIcon className={`w-3.5 h-3.5 ${overdue ? 'text-rose-400' : 'text-zinc-500'}`} />
      <input
        type="date"
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        className={`bg-transparent border border-zinc-800 rounded-sm px-2 py-1 text-xs w-[140px] focus:outline-none focus:border-amber-500 ${
          overdue ? 'text-rose-300' : 'text-zinc-300'
        }`}
      />
    </div>
  );
}

function TodoRow({ todo, ownerAccent, onUpdate, onDelete, onOpen, dragProps }) {
  const [title, setTitle] = useState(todo.title);
  const taRef = useRef(null);
  useEffect(() => { setTitle(todo.title); }, [todo.title]);

  // Auto-resize the textarea so long URLs / multi-line titles are fully visible
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
  }, [title]);

  const commitTitle = () => {
    const trimmed = title.trim();
    if (!trimmed) { setTitle(todo.title); return; }
    if (trimmed !== todo.title) onUpdate({ title: trimmed });
  };

  const copyTitle = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(todo.title || '');
      toast.success('Copié');
    } catch {
      toast.error('Impossible de copier');
    }
  };

  const commentCount = (todo.comments || []).length;
  const fileCount = todo.file_count || 0;

  const statusMeta = STATUS_COLORS[todo.status] || STATUS_COLORS.todo;
  // Status color owns the row when the task is out of the neutral "todo" state.
  // In "todo" state we keep the neutral row + the owner-board accent border so
  // you can still tell at a glance which board an item belongs to.
  const rowBg = todo.status === 'todo' ? 'bg-zinc-900/60 hover:bg-zinc-900' : statusMeta.row;
  const rowBorder = todo.status === 'todo' ? ownerAccent : statusMeta.border;

  return (
    <div
      className={`group grid grid-cols-12 gap-2 items-start border-l-2 ${rowBorder} ${rowBg} rounded-sm px-3 py-2 transition`}
      data-testid={`todo-row-${todo.id}`}
      {...dragProps}
    >
      <div className="col-span-1 flex items-center gap-1 min-w-0 pt-1">
        <GripVertical className="w-3.5 h-3.5 text-zinc-700 cursor-grab" />
      </div>

      <div className="col-span-11 sm:col-span-5 min-w-0 flex items-start gap-1">
        {/* wrap: break-all so URLs/very-long words go on multiple lines instead of truncating */}
        <textarea
          ref={taRef}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={commitTitle}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); e.currentTarget.blur(); } }}
          rows={1}
          className="w-full bg-transparent text-sm text-zinc-100 border-none focus:outline-none focus:ring-0 resize-none break-all whitespace-pre-wrap leading-relaxed py-0 overflow-hidden"
          style={{ minHeight: '1.5rem' }}
          data-testid={`todo-title-${todo.id}`}
        />
        <div className="flex items-center gap-0.5 shrink-0 pt-0.5">
          <button
            onClick={copyTitle}
            title="Copier le titre"
            className="p-1 text-zinc-500 hover:text-amber-400 opacity-0 group-hover:opacity-100 transition"
            data-testid={`todo-copy-${todo.id}`}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
            title="Détail / commentaires / fichiers"
            className="p-1 text-zinc-500 hover:text-amber-400 relative"
            data-testid={`todo-open-${todo.id}`}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            {(commentCount + fileCount) > 0 && (
              <span className="absolute -top-1 -right-1 bg-amber-500 text-zinc-950 text-[9px] leading-none rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-1 font-bold">
                {commentCount + fileCount}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="col-span-6 sm:col-span-3 flex items-center pt-1">
        <StatusPill value={todo.status} onChange={(s) => onUpdate({ status: s })} />
      </div>
      <div className="col-span-5 sm:col-span-2 flex items-center pt-1">
        <DueDateCell value={todo.due_date} onChange={(d) => onUpdate({ due_date: d })} />
      </div>
      <div className="col-span-1 flex items-center justify-end opacity-0 group-hover:opacity-100 transition pt-1">
        <button
          onClick={onDelete}
          className="p-1 text-zinc-500 hover:text-rose-400"
          title="Supprimer"
          data-testid={`todo-delete-${todo.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Detail drawer — full title, comments, attachments
// ────────────────────────────────────────────────────────────────────────
function TodoDetail({ todo, open, onClose, onDirty }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    if (!todo?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/todos/${todo.id}`);
      setDetail(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, [todo?.id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const copyFull = async () => {
    try {
      await navigator.clipboard.writeText(detail?.title || todo?.title || '');
      toast.success('Titre copié');
    } catch {
      toast.error('Copie impossible');
    }
  };

  const addComment = async () => {
    const t = commentText.trim();
    if (!t) return;
    setPosting(true);
    try {
      const { data: c } = await api.post(`/todos/${todo.id}/comments`, { text: t });
      setDetail(prev => ({ ...prev, comments: [...(prev.comments || []), c] }));
      setCommentText('');
      onDirty?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur ajout commentaire');
    } finally {
      setPosting(false);
    }
  };

  const deleteComment = async (cid) => {
    if (!window.confirm('Supprimer ce commentaire ?')) return;
    setDetail(prev => ({ ...prev, comments: prev.comments.filter(c => c.id !== cid) }));
    try {
      await api.delete(`/todos/${todo.id}/comments/${cid}`);
      onDirty?.();
    } catch (err) {
      toast.error('Erreur suppression');
      load();
    }
  };

  const uploadFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        await api.post(`/files/upload?entity_type=todo&entity_id=${todo.id}&category=todo`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      toast.success(`${files.length} fichier(s) ajouté(s)`);
      await load();
      onDirty?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const downloadFile = (fid) => {
    const token = localStorage.getItem('pp_access_token');
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/download/${fid}?auth=${encodeURIComponent(token || '')}`, '_blank');
  };

  const deleteFile = async (fid) => {
    if (!window.confirm('Supprimer cette pièce jointe ?')) return;
    setDetail(prev => ({ ...prev, files: prev.files.filter(f => f.id !== fid) }));
    try {
      await api.delete(`/files/${fid}`);
      onDirty?.();
    } catch (err) {
      toast.error('Erreur suppression');
      load();
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg bg-zinc-950 border-zinc-800 text-zinc-100 overflow-y-auto"
        data-testid="todo-detail-drawer"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <h3 className="text-sm font-semibold text-zinc-100">Détail de la tâche</h3>
          <button onClick={onClose} className="p-1 text-zinc-500 hover:text-zinc-100">
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading && <div className="text-xs text-zinc-500">Chargement…</div>}
        {detail && !loading && (
          <div className="space-y-6">
            {/* Full title */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Tâche</label>
                <button
                  onClick={copyFull}
                  className="text-[11px] text-amber-400 hover:text-amber-300 inline-flex items-center gap-1"
                  data-testid="detail-copy-title"
                >
                  <Copy className="w-3 h-3" /> Copier
                </button>
              </div>
              <p className="text-sm text-zinc-100 bg-zinc-900 border border-zinc-800 rounded-sm p-3 break-all whitespace-pre-wrap leading-relaxed" data-testid="detail-title">
                {detail.title}
              </p>
            </div>

            {/* Comments */}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1 mb-2">
                <MessageSquare className="w-3 h-3" /> Commentaires ({detail.comments.length})
              </label>
              <div className="space-y-2 mb-3">
                {detail.comments.map(c => (
                  <div key={c.id} className="bg-zinc-900 border border-zinc-800 rounded-sm p-2.5 group" data-testid={`comment-${c.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-zinc-100 break-words whitespace-pre-wrap leading-relaxed">{c.text}</p>
                        <p className="text-[10px] text-zinc-500 mt-1">
                          {c.author_name} · {new Date(c.created_at).toLocaleString('fr-FR')}
                        </p>
                      </div>
                      <button
                        onClick={() => deleteComment(c.id)}
                        className="p-1 text-zinc-600 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition"
                        title="Supprimer"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
                {detail.comments.length === 0 && (
                  <p className="text-[11px] text-zinc-600 italic">Aucun commentaire</p>
                )}
              </div>
              <div className="flex items-start gap-2">
                <Textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) addComment(); }}
                  rows={2}
                  placeholder="Ajouter un commentaire… (Ctrl/⌘+Entrée pour envoyer)"
                  className="bg-zinc-900 border-zinc-800 text-xs rounded-sm flex-1 min-h-[60px]"
                  data-testid="new-comment-input"
                />
                <Button
                  size="sm"
                  onClick={addComment}
                  disabled={!commentText.trim() || posting}
                  className="bg-amber-500 hover:bg-amber-600 text-zinc-950 h-8 text-xs rounded-sm"
                  data-testid="new-comment-submit"
                >
                  <Send className="w-3 h-3" />
                </Button>
              </div>
            </div>

            {/* Attachments */}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1 mb-2">
                <Paperclip className="w-3 h-3" /> Pièces jointes ({detail.files.length})
              </label>
              <div className="space-y-1.5 mb-3">
                {detail.files.map(f => (
                  <div key={f.id} className="flex items-center justify-between gap-2 bg-zinc-900 border border-zinc-800 rounded-sm px-2.5 py-1.5 group" data-testid={`file-${f.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                      <span className="text-xs text-zinc-200 truncate" title={f.original_filename}>{f.original_filename}</span>
                      <span className="text-[10px] text-zinc-600 shrink-0">{fmtBytes(f.size)}</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={() => downloadFile(f.id)}
                        className="p-1 text-zinc-500 hover:text-amber-400"
                        title="Télécharger"
                        data-testid={`file-download-${f.id}`}
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => deleteFile(f.id)}
                        className="p-1 text-zinc-500 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition"
                        title="Supprimer"
                        data-testid={`file-delete-${f.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
                {detail.files.length === 0 && (
                  <p className="text-[11px] text-zinc-600 italic">Aucune pièce jointe</p>
                )}
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded-sm px-3 py-2 text-xs text-zinc-200 transition" data-testid="upload-file-btn">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                <span>{uploading ? 'Envoi…' : 'Ajouter un fichier'}</span>
                <input type="file" onChange={uploadFile} disabled={uploading} className="hidden" multiple />
              </label>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function OwnerBoard({ owner, items, onCreate, onUpdate, onDelete, onReorder, onOpen }) {
  const [newTitle, setNewTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const [dragId, setDragId] = useState(null);

  const submitNew = async () => {
    const t = newTitle.trim();
    if (!t) return;
    setAdding(true);
    try {
      await onCreate({ title: t });
      setNewTitle('');
    } finally {
      setAdding(false);
    }
  };

  const handleDragStart = (id) => (e) => { setDragId(id); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = () => (e) => { e.preventDefault(); };
  const handleDrop = (targetId) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const ids = items.map(i => i.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    if (from === -1 || to === -1) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setDragId(null);
    onReorder(ids);
  };

  const counts = STATUS_ORDER.reduce((acc, s) => ({ ...acc, [s]: items.filter(i => i.status === s).length }), {});

  return (
    <Card className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid={`board-${owner.key}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className={`inline-block w-2 h-2 rounded-full ${owner.dot}`} />
            <h2 className="text-lg font-bold text-zinc-100" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {owner.label}
            </h2>
            <span className="text-xs text-zinc-500">{`${items.length} tâche${items.length > 1 ? 's' : ''}`}</span>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-zinc-500 flex-wrap">
            {STATUS_ORDER.map(s => (
              <span key={s} className="flex items-center gap-1">
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${STATUS_COLORS[s].bg}`} />
                <span>{counts[s]}</span>
              </span>
            ))}
          </div>
        </div>

        <div className="hidden sm:grid grid-cols-12 gap-2 items-center px-3 py-1 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-900">
          <div className="col-span-1" />
          <div className="col-span-5">Tâche</div>
          <div className="col-span-3">Statut</div>
          <div className="col-span-2">Échéance</div>
          <div className="col-span-1" />
        </div>

        <div className="space-y-1.5">
          {items.map(t => (
            <TodoRow
              key={t.id}
              todo={t}
              ownerAccent={owner.accent}
              onUpdate={(patch) => onUpdate(t.id, patch)}
              onDelete={() => onDelete(t.id)}
              onOpen={() => onOpen(t)}
              dragProps={{
                draggable: true,
                onDragStart: handleDragStart(t.id),
                onDragOver: handleDragOver(t.id),
                onDrop: handleDrop(t.id),
              }}
            />
          ))}
          {items.length === 0 && (
            <div className="text-center py-8 text-xs text-zinc-600 border border-dashed border-zinc-800 rounded-sm">
              Aucune tâche — ajoutez-en une ci-dessous
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 pt-2 border-t border-zinc-900">
          <Plus className="w-4 h-4 text-zinc-500 shrink-0" />
          <Input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitNew(); }}
            placeholder={`Nouvelle tâche pour ${owner.label}…`}
            className="bg-zinc-900 border-zinc-800 h-8 text-sm rounded-sm flex-1 text-zinc-100 placeholder:text-zinc-600"
            data-testid={`new-todo-input-${owner.key}`}
          />
          <Button
            size="sm"
            onClick={submitNew}
            disabled={!newTitle.trim() || adding}
            className="bg-amber-500 hover:bg-amber-600 text-zinc-950 h-8 text-xs rounded-sm px-3"
            data-testid={`new-todo-submit-${owner.key}`}
          >
            Ajouter
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function TodoPage() {
  const [data, setData] = useState({ arthur: [], sebastien: [] });
  const [loading, setLoading] = useState(true);
  const [openTodo, setOpenTodo] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/todos');
      setData({
        arthur: data.arthur || [],
        sebastien: data.sebastien || [],
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Impossible de charger la to-do list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createTodo = async (ownerKey, patch) => {
    try {
      const { data: created } = await api.post('/todos', { owner: ownerKey, ...patch });
      setData(prev => ({ ...prev, [ownerKey]: [...prev[ownerKey], created] }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur création');
    }
  };

  const updateTodo = async (ownerKey, id, patch) => {
    setData(prev => ({
      ...prev,
      [ownerKey]: prev[ownerKey].map(t => (t.id === id ? { ...t, ...patch } : t)),
    }));
    try {
      await api.patch(`/todos/${id}`, patch);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur mise à jour');
      load();
    }
  };

  const deleteTodo = async (ownerKey, id) => {
    if (!window.confirm('Supprimer cette tâche ?')) return;
    setData(prev => ({ ...prev, [ownerKey]: prev[ownerKey].filter(t => t.id !== id) }));
    try {
      await api.delete(`/todos/${id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur suppression');
      load();
    }
  };

  const reorderBoard = async (ownerKey, ids) => {
    const map = new Map(data[ownerKey].map(t => [t.id, t]));
    const reordered = ids.map(id => map.get(id)).filter(Boolean);
    setData(prev => ({ ...prev, [ownerKey]: reordered }));
    try {
      await api.post('/todos/reorder', { owner: ownerKey, ids });
    } catch {
      toast.error('Erreur réorganisation');
      load();
    }
  };

  return (
    <div className="space-y-6" data-testid="todo-page">
      <div>
        <h1
          className="text-3xl sm:text-4xl font-bold text-zinc-100"
          style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
        >
          To-Do List
        </h1>
        <p className="text-xs text-zinc-500 mt-1">Tableaux privés — visibles uniquement par les administrateurs</p>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Chargement…</div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {OWNERS.map(o => (
            <OwnerBoard
              key={o.key}
              owner={o}
              items={data[o.key] || []}
              onCreate={(patch) => createTodo(o.key, patch)}
              onUpdate={(id, patch) => updateTodo(o.key, id, patch)}
              onDelete={(id) => deleteTodo(o.key, id)}
              onReorder={(ids) => reorderBoard(o.key, ids)}
              onOpen={(t) => setOpenTodo(t)}
            />
          ))}
        </div>
      )}

      <TodoDetail
        todo={openTodo}
        open={!!openTodo}
        onClose={() => setOpenTodo(null)}
        onDirty={load}
      />
    </div>
  );
}
